import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";

interface Option {
  value: number;
  label: string;
}

interface SearchableSelectProps {
  id?: string;
  options: Option[];
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-errormessage"?: string;
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: { position: "relative" },
  input: { width: "100%", boxSizing: "border-box" },
  dropdown: {
    position: "fixed" as const,
    maxHeight: 200,
    overflowY: "auto" as const,
    background: "#fff",
    border: "1px solid #d8e1ec",
    borderTop: "none",
    borderRadius: "0 0 10px 10px",
    zIndex: 9999,
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  item: {
    padding: "7px 12px",
    fontSize: 13,
    cursor: "pointer",
    color: "#23324a",
  },
  itemHighlighted: {
    background: "#f0f5ff",
  },
  noResults: {
    padding: "7px 12px",
    fontSize: 13,
    color: "#64748b",
    fontStyle: "italic",
  },
};

export default function SearchableSelect({
  id,
  options,
  value,
  onChange,
  placeholder = "Search...",
  disabled = false,
  className,
  "data-testid": testId,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-errormessage": ariaErrorMessage,
}: SearchableSelectProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listboxIdRef = useRef(
    id ? `${id}-listbox` : `searchableSelectListbox-${Math.random().toString(36).slice(2)}`,
  );
  const listboxId = listboxIdRef.current;

  // Sync display text when value changes externally
  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const handleSelect = useCallback(
    (opt: Option) => {
      onChange(opt.value);
      setQuery("");
      setOpen(false);
      setHighlightIdx(-1);
    },
    [onChange],
  );

  const handleClear = () => {
    onChange(null);
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setHighlightIdx((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter" && highlightIdx >= 0 && filtered[highlightIdx]) {
      handleSelect(filtered[highlightIdx]);
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      setHighlightIdx(-1);
    }
  };

  // Update dropdown position when open
  useEffect(() => {
    if (open && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, [open]);

  // Close on page scroll (dropdown is fixed-position, would detach from input)
  useEffect(() => {
    if (!open) return;
    const handleScroll = (e: Event) => {
      // Don't close when scrolling inside the dropdown itself
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setOpen(false);
      setQuery("");
      setHighlightIdx(-1);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current && !wrapperRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setQuery("");
        setHighlightIdx(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      <input
        id={id}
        ref={inputRef}
        className={className}
        type="text"
        value={open ? query : selectedLabel}
        placeholder={value ? selectedLabel : placeholder}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlightIdx(-1);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          setQuery("");
        }}
        onClick={() => {
          if (!open) {
            setOpen(true);
            setQuery("");
          }
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-errormessage={ariaErrorMessage}
        data-testid={testId}
      />
      {open && !disabled && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{ ...styles.dropdown, top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
          id={listboxId}
          role="listbox"
        >
          {value && (
            <div
              style={{ ...styles.item, color: "#94a3b8", borderBottom: "1px solid #f1f5f9" }}
              onClick={handleClear}
              role="option"
              aria-selected={false}
            >
              ✕ Clear selection
            </div>
          )}
          {filtered.length === 0 ? (
            <div style={styles.noResults}>No matches</div>
          ) : (
            filtered.map((opt, i) => (
              <div
                key={opt.value}
                style={{
                  ...styles.item,
                  ...(i === highlightIdx ? styles.itemHighlighted : {}),
                  ...(opt.value === value ? { fontWeight: 700 } : {}),
                }}
                onClick={() => handleSelect(opt)}
                onMouseEnter={() => setHighlightIdx(i)}
                role="option"
                aria-selected={opt.value === value}
                data-value={opt.value}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
